// import { useQuery } from '@tanstack/react-query'; // re-enable for Dashboard sub-site dropdown

import { Bell, Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router-dom';
import IconLogout from '@/assets/icon/icon-db-logout.svg';
import IconUser from '@/assets/icon/icon-db-people.svg';
import IconCalendar from '@/assets/pedestrian/icon/icon-calendar.svg';
import IconTime from '@/assets/pedestrian/icon/icon-clock.svg';
import Logo from '@/assets/smartcity/logo.svg';
import StoreLogo from '@/assets/store/icon/logo.svg';
// import LogoLight from '@/assets/smartcity/logo-light.svg';
import useAuth from '@/domains/auth/hooks/useAuth';
import ModalProfileManagement from '@/domains/profile-management/components/ModalProfileManagement/ModalProfileManagement';
import { useUserManagement } from '@/domains/system-management/hooks/useUserManagement';
import SwitchButton from '@/shared/components/ui/SwitchButton/SwitchButton';
import { cn } from '@/lib/utils';
import LogoutConfirmModal from '@/shared/components/layout/LogoutConfirmModal/LogoutConfirmModal';
import type { NavbarItem } from '@/shared/components/layout/Sidebar/Sidebar.types';
import UserProfile from '@/shared/components/layout/UserProfile/UserProfile';
import { Button } from '@/shared/components/ui/Button';
import ThemeToggle from '@/shared/components/ui/ThemeToggle/ThemeToggle';
import { DASHBOARD_TABS, MANAGEMENT_SECTIONS } from '@/shared/config/headerTabs';
import { URLS } from '@/shared/config/urls';
import { usePreferenceContext } from '@/shared/context/preferenceContext';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import i18n from '@/shared/utils/i18n';
import { getFirstAccessibleRoute, getFirstManagementRoute } from '@/shared/utils/navigation';
import Common from '@/shared/utils/standard';
import { useAuthStore } from '@/store/authStore';
import { useThemeStore } from '@/store/themeStore';
import type { HeaderProps, NavigationItem } from './Header.types';
import { LogOut, Undo2 } from 'lucide-react';

// const dataHeader = [
//   { name: 'Integrated Smart City', link: '/dashboard/smartCity-dashboard' },
//   { name: 'Traffic', link: '/dashboard/psc-dashboard' },
//   { name: 'PSC', link: '/dashboard/psc-dashboard' },
//   { name: 'CCTV', link: 'dashboard/ambulance-dashboard' },
//   { name: 'management', link: '/manage-dashboard' },
// ];

// `name` is the stable key used for access checks; `tag` is the cm_language
// tag the tab is rendered with (eng/ind/kor rows in 51.cm_language_insert.sql).
// Tab routes come from the shared model so they cannot drift from the route
// guard in shared/utils/navigation.ts.
const dataHeader = [
  ...DASHBOARD_TABS.map((tab) => ({
    name: tab.name,
    tag: tab.tag,
    link: tab.grant,
    activePaths: tab.paths,
  })),
  {
    name: 'Management',
    tag: 'cmn>nav>management',
    // Placeholder only - the rendered tab uses `managementRoute` below.
    link: '/system-management/program',
    activePaths: ['/management', ...MANAGEMENT_SECTIONS],
  },
];

const Header = ({ className, onMenuClick }: HeaderProps) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { user_id } = useAuthStore();
  const { useUser } = useUserManagement();
  const { data: userInfo } = useUser(user_id);
  const { preferenceData } = usePreferenceContext();
  const [time, setTime] = useState<string>('');
  const [date, setDate] = useState<string>('');
  const [day, setDay] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // Nav tabs collapse into a drawer below `lg` (1150px) - see visibleHeaderItems
  const [isNavOpen, setIsNavOpen] = useState<boolean>(false);
  const navRef = useRef<HTMLDivElement>(null);
  const { handleAuthority } = useAuth({ userID: user_id });

  const [optionParams] = useState<string>('upper-code=021&upper-code=ORG');
  const { commonListData } = useCommonCodes({ optionParams });

  // Authority Management (system-management/authority) grants each group a
  // set of accessible programs; handleAuthority is that set for the current
  // user (same navbarList Sidebar.tsx uses to build the management menu).
  // Management has no single page behind it, so the tab carries the first
  // section this user is actually authorized for instead of a fixed route -
  // a group whose grants cover only some sections still lands on a usable page.
  const managementRoute = getFirstManagementRoute(handleAuthority);
  // Dashboard tabs (Smart City/Traffic/PSC/CCTV) are the DASHBOARD group's own
  // children - their `link` matches the url a child Program authorizes, so
  // gate each tab on that url being present rather than showing all four
  // unconditionally (e.g. a PSC-only group should not see Smart City/Traffic).
  const authorizedDashboardUrls = new Set(
    (handleAuthority ?? []).flatMap((item) => item.urls ?? []).filter((url): url is string => !!url)
  );
  const visibleHeaderItems = dataHeader.flatMap((item) => {
    if (item.name === 'Management') {
      return managementRoute ? [{ ...item, link: managementRoute }] : [];
    }
    return authorizedDashboardUrls.has(item.link) ? [item] : [];
  });
  const logoRoute = getFirstAccessibleRoute(handleAuthority) ?? location.pathname;

  const navLabel = (item: (typeof dataHeader)[number]): string => t(item.tag);

  const [logoutOpen, setLogoutOpen] = useState(false);

  // Initialize language and set up date/time updates
  // Initialize language and set up date/time updates
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const seconds = now.getSeconds().toString().padStart(2, '0');

      // eng | ind only; anything else (e.g. leftover 'kor' session) falls back to eng
      const lang = i18n.language === 'eng' ? 'eng' : 'ind';
      const langSource = Common.getDTPLangSource(lang);
      const dayName = langSource?.days[now.getDay()] || '';
      const monthName = langSource?.months[now.getMonth()] || '';
      const day = now.getDate();
      const year = now.getFullYear();

      setTime(`${hours}:${minutes}:${seconds}`);
      if (lang === 'eng') {
        setDate(`${monthName} ${day}, ${year}`); // August 11, 2026
      } else {
        setDate(`${day} ${monthName} ${year}`); // 11 Agustus 2026
      }
      setDay(dayName);
    };

    updateDateTime();
    const intervalId = setInterval(updateDateTime, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const isPathActive = (activePaths: string[]) =>
    activePaths.some((path) => location.pathname.startsWith(path));

  // Get title from preferences
  const title = preferenceData?.find((item) => item.preference_key === 'title')?.preference_value;
  // Get navigation logo from preferences (uploaded via System Management)
  const navLogo = preferenceData?.find(
    (item) => item.preference_key === 'nav_logo'
  )?.preference_value;

  const isDark = useThemeStore((state) => state.isDark);

  return (
    <>
      <header
        className={cn(
          'w-full  flex flex-row border-b justify-between h-[56px] lg:h-[62px] text-text-white bg-white md:bg-db-black',
          className
        )}
      >

        <div className="flex md:hidden w-full items-center justify-between px-4">
          <button
            type="button"
            onClick={onMenuClick}
            aria-label="Buka menu"
            className="p-1 -ml-1 text-black"
          >
            <Menu size={26} />
          </button>
          <img src={StoreLogo} alt="rumah kebijaksanaan" className="h-7 w-auto" />
        </div>

        {/* Right side: Date/Time and User info (desktop only; on mobile these live in the sidebar drawer) */}
        <div className="hidden md:flex gap-[8px] w-fit ml-auto shrink-0">
          {/* Below md the date/time moves into the drawer instead of squeezing the bar */}
          <div className="hidden md:flex w-fit flex-row h-full items-center gap-[24px]">
            {/* Time display */}
            <div className="flex flex-row items-center gap-[8px] xl:gap-[12px] bg-[#E2E8F0] px-3 py-1 rounded-[4px]">
              <span className="hidden xl:inline text-db-text title2 whitespace-nowrap">
                {date} <span className="text-db-text title2">({day})</span>
              </span>
              <span className="title2medium text-db-text">{time}</span>
            </div>
          </div>

          {/* User info and logout */}
          <div className="flex items-center h-full min-w-0 md:min-w-[218px] bg-header-content justify-between px-[10px] md:px-[20px] gap-[8px] md:gap-[12px]">
            <button type='button' className="inline-flex gap-3 items-center w-fit">
              <UserProfile nameClassName="text-text-grey" />
            </button>

            <div className="flex items-center gap-[4px]">
              <Button
                variant="ghost"
                type="button"
                className="h-auto p-0 hover:bg-transparent"
                onClick={() => setLogoutOpen(true)}
              >
                <LogOut color='red' size={24} />
              </Button>
            </div>
          </div>
        </div>
      </header>
      <LogoutConfirmModal open={logoutOpen} onClose={() => setLogoutOpen(false)} />

      {userInfo?.account_id && (
        <ModalProfileManagement
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          userInfo={userInfo as unknown as { account_id: string;[key: string]: any }}
          commonData={commonListData}
        />
      )}
    </>
  );
};

export default Header;
